import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const STEPS = [
  {
    icon: '💪',
    titolo: 'Benvenuto in FitProgress',
    testo: 'La piattaforma di coaching per calisthenics e strength training. Tieni traccia dei tuoi progressi, segui le sessioni del tuo coach e migliora ogni giorno.',
    colore: 'var(--accent)',
  },
  {
    icon: '🎯',
    titolo: 'Tracking personale',
    // RPE non esiste più: è stato rimosso dall'app e dal database, sostituito dal
    // numero di SET. Era rimasto scritto solo qui, e l'onboarding prometteva una
    // funzione che il nuovo utente poi non trovava da nessuna parte.
    testo: 'Registra ogni esercizio con ripetizioni, serie, carico ed elastico. Visualizza i tuoi grafici di progresso nel tempo e scopri quanto sei migliorato.',
    colore: 'var(--accent2)',
  },
  {
    icon: '👥',
    titolo: 'Allenati con il tuo coach',
    testo: 'Il coach crea sessioni EMOM, Tabata, AMRAP e le assegna al calendario. Ogni giorno trovi la sessione pronta — eseguila e registra i tuoi risultati.',
    colore: 'var(--green)',
  },
  {
    icon: '📈',
    titolo: 'Monitora i progressi',
    testo: 'Ogni sessione completata viene salvata. Il coach vede i tuoi risultati, tu vedi la tua crescita nel tempo. I dati parlano da soli.',
    colore: 'var(--purple)',
  },
  {
    icon: '🚀',
    titolo: 'Come vuoi iniziare?',
    testo: 'FitProgress è gratuito: allenati da solo o con un gruppo, oppure crea il tuo gruppo come coach e segui fino a 3 atleti. Scegli il tuo profilo per continuare.',
    colore: 'var(--accent3)',
  },
]

export default function Onboarding({ onCompleta }) {
  const { caricaProfilo } = useAuth()
  const [step, setStep] = useState(0)
  const [salvataggio, setSalvataggio] = useState(false)
  const [ruoloSelezionato, setRuoloSelezionato] = useState(null) // selezionato ma non ancora confermato

  function avanti() {
    if (step < STEPS.length - 1) {
      setStep(step + 1)
    }
  }

  async function salta() {
    setSalvataggio(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        await supabase.from('profili').update({ ruolo_scelto: true }).eq('id', session.user.id)
        await caricaProfilo(session.user.id)
      }
    } catch {
      // In caso di errore l'utente entra comunque: il piano di default resta "atleta"
    }
    setSalvataggio(false)
    onCompleta()
  }

  async function confermaRuolo() {
    if (!ruoloSelezionato) return
    setSalvataggio(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        await supabase.from('profili').update({ piano: ruoloSelezionato, ruolo_scelto: true }).eq('id', session.user.id)
        await caricaProfilo(session.user.id)
      }
    } catch {
      // In caso di errore l'utente entra comunque: il piano di default resta "atleta"
    }
    setSalvataggio(false)
    onCompleta()
  }

  const s = STEPS[step]
  const ultimoStep = step === STEPS.length - 1

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '40px 28px', maxWidth: 430, margin: '0 auto' }}>

      {/* Skip */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={salta} disabled={salvataggio} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer', padding: '4px 8px' }}>
          Salta
        </button>
      </div>

      {/* Contenuto */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 24 }} className="fade-in" key={step}>

        {/* Icona */}
        <div style={{ width: 100, height: 100, borderRadius: '50%', background: `rgba(${s.colore === 'var(--accent)' ? '127,214,61' : s.colore === 'var(--accent2)' ? '87,184,255' : s.colore === 'var(--green)' ? '74,222,128' : s.colore === 'var(--purple)' ? '199,125,255' : '255,120,87'},0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>
          {s.icon}
        </div>

        {/* Titolo */}
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, color: 'var(--text)', lineHeight: 1.3 }}>
          {s.titolo}
        </div>

        {/* Testo */}
        <div style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 320 }}>
          {s.testo}
        </div>

        {/* Scelta ruolo — solo nell'ultimo step: prima si seleziona, poi si conferma */}
        {ultimoStep && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 300, marginTop: 8 }}>
            <button
              disabled={salvataggio}
              onClick={() => setRuoloSelezionato('atleta')}
              style={{
                fontSize: 15, padding: '14px', borderRadius: 10, cursor: 'pointer',
                border: ruoloSelezionato === 'atleta' ? '2px solid var(--accent)' : '2px solid var(--border)',
                background: ruoloSelezionato === 'atleta' ? 'rgba(127,214,61,0.12)' : 'transparent',
                color: 'var(--text)', fontWeight: ruoloSelezionato === 'atleta' ? 700 : 500,
              }}
            >
              {ruoloSelezionato === 'atleta' ? '✅ ' : ''}🏋️ Mi alleno (Atleta)
            </button>
            <button
              disabled={salvataggio}
              onClick={() => setRuoloSelezionato('coach')}
              style={{
                fontSize: 15, padding: '14px', borderRadius: 10, cursor: 'pointer',
                border: ruoloSelezionato === 'coach' ? '2px solid var(--accent)' : '2px solid var(--border)',
                background: ruoloSelezionato === 'coach' ? 'rgba(127,214,61,0.12)' : 'transparent',
                color: 'var(--text)', fontWeight: ruoloSelezionato === 'coach' ? 700 : 500,
              }}
            >
              {ruoloSelezionato === 'coach' ? '✅ ' : ''}👨‍🏫 Gestisco atleti (Coach)
            </button>

            <button
              className="btn btn-primary"
              disabled={!ruoloSelezionato || salvataggio}
              onClick={confermaRuolo}
              style={{ fontSize: 15, padding: '14px', marginTop: 4, opacity: !ruoloSelezionato ? 0.5 : 1 }}
            >
              {salvataggio ? 'Attendere...' : 'Conferma'}
            </button>

            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              Potrai cambiare idea in seguito scrivendo allo staff.
            </div>
          </div>
        )}
      </div>

      {/* Dots indicatori */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
        {STEPS.map((_, i) => (
          <div key={i} onClick={() => setStep(i)} style={{ width: i === step ? 20 : 8, height: 8, borderRadius: 4, background: i === step ? s.colore : 'var(--border)', transition: 'all 0.3s', cursor: 'pointer' }} />
        ))}
      </div>

      {/* Pulsante avanti — nascosto nell'ultimo step, sostituito dalla scelta ruolo */}
      {!ultimoStep && (
        <button className="btn btn-primary" onClick={avanti} style={{ fontSize: 16 }}>
          Avanti
        </button>
      )}
    </div>
  )
}
